import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Menu, X, FileText, Scale, Building2, Shield, Briefcase, Bell, Home as HomeIcon, Package, Flame, BookOpen, CheckCircle, Download, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mainCategories, mockFeatures, mockStats } from '../data/mockData';
import { SocialProofTicker } from '../components/SocialProofTicker';
import { ExitIntentPopup } from '../components/ExitIntentPopup';
import { HorizontalScroll, ScrollCard } from '../components/HorizontalScroll';
import { BookMockup, getBadge } from '../components/BookMockup';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [featuredGuides, setFeaturedGuides] = useState([]);
  const [featuredBundles, setFeaturedBundles] = useState([]);
  const [categoryCovers, setCategoryCovers] = useState({});

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const [prodResp, bundleResp] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/bundles`),
        ]);
        const guides = (prodResp.data.products || []).filter(p => p.product_type === 'guide');
        // Prioritize trending, then shuffle
        const trending = guides.filter(g => g.trending);
        const rest = guides.filter(g => !g.trending).sort(() => Math.random() - 0.5);
        setFeaturedGuides([...trending.slice(0, 10), ...rest].slice(0, 20));
        setFeaturedBundles((bundleResp.data.bundles || []).slice(0, 12));

        // Build category cover map: pick the first guide from each category
        const coverMap = {};
        const categoryMap = {
          'credit-disputes': 'Credit Disputes',
          'business-credit': 'Business Credit',
          'court-motions': 'Court Motions',
          'affidavits': 'Affidavits',
          'trust-estate': 'Trust',
          'legal-notices': 'Legal Notices',
          'landlord-tenant': 'Landlord',
          'lawsuits': 'Lawsuits'
        };
        Object.entries(categoryMap).forEach(([slug, keyword]) => {
          const match = guides.find(g => g.category?.toLowerCase().includes(keyword.toLowerCase()));
          if (match) coverMap[slug] = match.id;
        });
        setCategoryCovers(coverMap);
      } catch (e) { console.error(e); }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Top Promo Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white py-2.5 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 flex items-center justify-center gap-2 flex-wrap">
          <span className="text-sm font-medium">
            New to legal paperwork? Get step-by-step guidance for any document you need
          </span>
          <a href="/browse" className="underline font-semibold hover:text-orange-100 transition-colors">
            Browse Guides
          </a>
        </div>
      </div>

      <Navbar />

      {/* Hero Section — Updated Copy */}
      <section className="relative bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-transparent to-red-500/20"></div>
        <div className="absolute -right-64 top-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-orange-400 to-orange-600 opacity-30 blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white space-y-8">
              <div className="hidden lg:block absolute left-8 top-24 w-48 h-64 rounded-2xl overflow-hidden shadow-2xl transform -rotate-6 hover:rotate-0 transition-transform duration-500 opacity-30">
                <img
                  src="https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRvY3VtZW50c3xlbnwwfHx8fDE3NzM1OTkyMDB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional collaboration"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-4">
                <div className="inline-block bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold animate-pulse">
                  140+ GUIDES
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Fix Your Credit, Handle Legal Issues &amp; Build Business Credit —{' '}
                  <span className="text-orange-400">Without Paying Thousands</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                  Step-by-step templates &amp; legal guides you can use immediately
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch gap-4">
                <Button
                  size="lg"
                  className="bg-black text-white hover:bg-gray-800 text-lg px-8 py-6 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                  onClick={() => window.location.href = '/browse'}
                  data-testid="hero-browse-btn"
                >
                  Start Here
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6 rounded-lg transition-all duration-300"
                  onClick={() => window.location.href = '/bundles'}
                  data-testid="hero-bundles-btn"
                >
                  View Bundle Deals
                </Button>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white rounded-lg px-5 py-3 hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto"
                  data-testid="hero-amazon-btn"
                >
                  <span className="text-orange-500 text-sm font-bold">Prefer Kindle ?</span>
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/0246eb6a-91e0-4a0a-b5e1-05578c0f729b/images/699b83156390efd44d7cf24f6278cf58a802c4115802ad11b7337bd37006d495.png"
                    alt="Amazon"
                    className="h-6 w-auto"
                  />
                </a>
              </div>

              {/* Trust Line */}
              <div className="flex items-center gap-4 text-sm text-gray-300 flex-wrap">
                <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-400" /> 140+ guides</span>
                <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-400" /> Instant downloads</span>
                <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-400" /> Used for real-world legal situations</span>
              </div>
            </div>

            {/* Right Content - Document Preview Card */}
            <div className="relative">
              <div className="hidden lg:block absolute right-8 bottom-24 w-48 h-64 rounded-2xl overflow-hidden shadow-2xl transform rotate-6 hover:rotate-0 transition-transform duration-500">
                <img
                  src="https://images.unsplash.com/photo-1762341118920-0b65e8d88aa2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG9mZmljZXxlbnwwfHx8fDE3NzM1OTkxOTZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional at work"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="relative z-10 max-w-sm mx-auto">
                <div className="bg-white rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-all duration-500">
                  <div className="space-y-6">
                    <div className="text-center space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900">Guides Starting at</h3>
                      <p className="text-3xl font-extrabold text-red-700">$11.99</p>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span>Step-by-step instructions</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span>Sample templates included</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span>Real-world examples</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span>Legal reference materials</span>
                      </div>
                    </div>

                    <Button
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white text-lg py-6 rounded-xl hover:from-orange-600 hover:to-orange-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() => window.location.href = '/browse'}
                    >
                      Preview Guides
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Not Sure Where to Start? */}
      <section className="py-12 bg-white border-b border-gray-100" data-testid="start-here-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Not sure where to start?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-red-700 text-white hover:bg-red-800 text-base px-6 py-5 rounded-lg shadow-md"
              onClick={() => window.location.href = '/category/credit-disputes'}
              data-testid="start-fix-credit-btn"
            >
              Fix My Credit
            </Button>
            <Button
              size="lg"
              className="bg-gray-900 text-white hover:bg-gray-800 text-base px-6 py-5 rounded-lg shadow-md"
              onClick={() => window.location.href = '/category/court-motions'}
              data-testid="start-legal-btn"
            >
              Handle a Legal Issue
            </Button>
            <Button
              size="lg"
              className="bg-orange-600 text-white hover:bg-orange-700 text-base px-6 py-5 rounded-lg shadow-md"
              onClick={() => window.location.href = '/category/business-credit'}
              data-testid="start-business-btn"
            >
              Build Business Credit
            </Button>
          </div>
        </div>
      </section>

      {/* Bundle Builder CTA */}
      <section className="py-10 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600 text-white" data-testid="bundle-builder-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-1">Build Your Own Bundle</h2>
            <p className="text-blue-100 text-sm">Pick any 5 guides from any category — only $39. Mix & match to fit your needs.</p>
          </div>
          <Button
            size="lg"
            className="bg-white text-blue-700 hover:bg-blue-50 font-bold text-base px-8 py-5 rounded-lg shadow-lg shrink-0"
            onClick={() => window.location.href = '/build-bundle'}
            data-testid="bundle-builder-cta-btn"
          >
            Start Building
          </Button>
        </div>
      </section>

      {/* How It Works Section — NEW */}
      <section className="py-16 bg-white" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              How <span className="text-orange-500">PaperworkDepot</span> Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1 */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Search className="w-8 h-8 text-white" />
              </div>
              <div className="text-xs font-bold text-orange-600 uppercase tracking-widest">Step 1</div>
              <h3 className="text-lg font-bold text-gray-900">Choose Your Guide</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Browse from 140+ legal and credit templates designed for real-world situations.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Download className="w-8 h-8 text-white" />
              </div>
              <div className="text-xs font-bold text-orange-600 uppercase tracking-widest">Step 2</div>
              <h3 className="text-lg font-bold text-gray-900">Download Instantly</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Get immediate access to your PDF guide with step-by-step instructions and ready-to-use templates.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div className="text-xs font-bold text-orange-600 uppercase tracking-widest">Step 3</div>
              <h3 className="text-lg font-bold text-gray-900">Use With Confidence</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Follow the guide to complete your documents correctly and take action with clarity.
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-10 max-w-2xl mx-auto">
            Designed for individuals and businesses who want clear, structured legal documentation without confusion.
          </p>
        </div>
      </section>

      {/* Main Categories Section — Horizontal Swipe */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HorizontalScroll title="Explore our documentation guides" subtitle="Browse by category to find the perfect guide for your needs">
            {mainCategories.map((category, index) => (
              <ScrollCard key={index}>
                <a
                  href={category.slug ? `/category/${category.slug}` : '/browse'}
                  className="block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-gray-100 hover:border-orange-300 h-full"
                  data-testid={`category-card-${category.slug}`}
                >
                  <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-3">
                    {categoryCovers[category.slug] ? (
                      <BookMockup
                        title={category.name}
                        productId={categoryCovers[category.slug]}
                        productType="guide"
                        size="md"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                        <category.icon className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 space-y-2">
                    <h3 className="font-bold text-sm text-gray-900 line-clamp-2">{category.name}</h3>
                    <p className="text-xs text-gray-500 line-clamp-2">{category.description}</p>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs font-semibold text-orange-600">{category.guideCount} guides</span>
                      {category.badge && (
                        <span className="bg-red-600 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                          {category.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </a>
              </ScrollCard>
            ))}
          </HorizontalScroll>
        </div>
      </section>

      {/* Featured Guides — Swipeable */}
      {featuredGuides.length > 0 && (
        <section className="py-12 bg-white" data-testid="featured-guides-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <HorizontalScroll title="Popular Guides" subtitle="Swipe to explore — trending templates used by thousands">
              {featuredGuides.map(guide => (
                <ScrollCard key={guide.id} width="w-52 sm:w-60">
                  <a
                    href={`/guide/${guide.id}`}
                    className="block bg-white rounded-xl shadow-md border border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    data-testid={`featured-guide-${guide.id}`}
                  >
                    <BookMockup productId={guide.id} title={guide.title} size="card" badge={getBadge(guide)} />
                    <div className="p-3">
                      <h3 className="text-xs font-bold text-gray-900 line-clamp-2 leading-tight group-hover:text-orange-600 transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-[10px] text-gray-400 mt-1">{guide.category?.split('&')[0]?.trim()}</p>
                    </div>
                  </a>
                </ScrollCard>
              ))}
            </HorizontalScroll>
          </div>
        </section>
      )}

      {/* Featured Bundles — Swipeable */}
      {featuredBundles.length > 0 && (
        <section className="py-12 bg-gray-50" data-testid="featured-bundles-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <HorizontalScroll title="Bundle Deals" subtitle="Save more with bundles — swipe to browse">
              {featuredBundles.map(bundle => (
                <ScrollCard key={bundle.id} width="w-56 sm:w-64">
                  <a
                    href={`/bundle/${bundle.id}`}
                    className="block bg-white rounded-xl shadow-md border border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    data-testid={`featured-bundle-${bundle.id}`}
                  >
                    <BookMockup productId={bundle.id} title={bundle.title} size="card" badge={getBadge(bundle)} />
                    <div className="p-3">
                      <h3 className="text-xs font-bold text-gray-900 line-clamp-2 leading-tight group-hover:text-orange-600 transition-colors">
                        {bundle.title}
                      </h3>
                      {bundle.savings_label && (
                        <p className="text-[10px] text-green-600 font-semibold mt-1">{bundle.savings_label}</p>
                      )}
                    </div>
                  </a>
                </ScrollCard>
              ))}
            </HorizontalScroll>
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-red-700 via-orange-600 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockStats.map((stat, index) => (
              <div key={index} className="text-center space-y-2">
                <div className="text-4xl md:text-5xl font-bold">{stat.value}</div>
                <div className="text-sm md:text-base text-white/90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white" id="why-templates-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why choose <span className="text-orange-500">PaperworkDepot?</span>
            </h2>
            <p className="text-base md:text-lg text-gray-600">Everything you need to understand and file legal documents correctly</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockFeatures.map((feature, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-md hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 border border-gray-200 hover:border-orange-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to master your <span className="text-orange-400">legal paperwork?</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300">
            Join thousands who trust PaperworkDepot for clear, structured documentation guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 text-lg px-8 py-6 rounded-lg shadow-xl"
              onClick={() => window.location.href = '/browse'}
            >
              Browse All 140+ Guides
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6 rounded-lg"
              onClick={() => window.location.href = '/bundles'}
            >
              View Bundle Deals
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Social Proof Ticker */}
      <SocialProofTicker />

      {/* Exit Intent Popup */}
      <ExitIntentPopup />
    </div>
  );
};

export default Home;
